// Takeoff quantities as CSV — by condition, by drawing and by room.
//
// All three are built from the SAME rows (the drawn / placed conditions and the
// rooms of one revision) through the SAME quantity function the canvas labels use
// (computeConditionQuantities), so they reconcile: the by-room and by-drawing
// files add up to the by-condition totals. Long form (one row per drawing x
// condition, one per room x condition) so they pivot straight in Excel.
//
// Quantity 1 is the condition's own unit reading — the one the estimate prices;
// Quantity 2 / 3 are its other populated readings (e.g. LF -> SF -> CF). Units are
// in their own column; nothing is converted or rounded beyond 3 decimals.
// A back-out (deduct polygon) is listed as a NEGATIVE area so the files agree
// with the net quantities the estimate prices.

import { computeConditionQuantities } from "../config/conditionCalcTypes";
import { polygonOf, polygonArea } from "./takeoffGeometry";
import { calcAreas } from "./takeoffMath";
import { convertQuantity, unitWord, FT_TO_M, SF_TO_M2 } from "./units";

export const NO_DRAWING = "(no drawing)";
export const NO_ROOM = "(no room)";
const BACK_OUT_CODE = "BACK-OUT";

// ── CSV ─────────────────────────────────────────────────────────────────────

// A text cell that starts with = + - @ (or a tab / CR) is read as a formula by
// Excel; prefix it with an apostrophe so imported data can never run one.
function guardText(s) {
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
}

export function csvCell(v) {
  if (v == null || v === "") return "";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  const s = guardText(String(v));
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** columns: [{ key, label }]; rows: objects. CRLF line endings (what Excel expects). */
export function toCsv(columns, rows) {
  const head = columns.map(c => csvCell(c.label)).join(",");
  const body = rows.map(r => columns.map(c => csvCell(r[c.key])).join(","));
  return [head, ...body].join("\r\n") + "\r\n";
}

export const csvFileName = (parts, ext = "csv") =>
  parts.filter(Boolean).join("_").replace(/[^A-Za-z0-9._-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") + "." + ext;

// ── quantities ──────────────────────────────────────────────────────────────

const r3 = (v) => (v == null || !Number.isFinite(+v) ? null : Math.round(+v * 1000) / 1000);
const sum = (a, b) => (a == null && b == null ? null : (a || 0) + (b || 0));

const isBackOutRow = (c) => c.MeasureRole === "deduct";

// One drawn row -> its identity and up to three readings. A back-out is a negative area.
function rowQuantities(c, units = "imperial") {
  if (isBackOutRow(c)) {
    let area = null;
    try { const g = typeof c.GeometryJson === "string" ? JSON.parse(c.GeometryJson) : c.GeometryJson; area = g?.areaFt2 ?? null; } catch { /* unreadable */ }
    return { code: BACK_OUT_CODE, name: "Back-out (deduct)", q: [convertQuantity({ q: area == null ? null : -area, u: "sf" }, units)] };
  }
  return { code: c.Code ?? "", name: c.Name ?? "", q: computeConditionQuantities(c).map(x => convertQuantity(x, units)) };
}

const conditionKey = (c) => {
  if (isBackOutRow(c)) return BACK_OUT_CODE;
  const id = c.Kind === "item" ? `i${c.ItemID}` : c.Kind === "assembly" ? `a${c.AssemblyID}` : c.TakeoffConditionTypeID != null ? `t${c.TakeoffConditionTypeID}` : `l${c.TakeoffConditionLibraryID}`;
  return `${id}|${c.Code ?? ""}`;
};

const natural = (a, b) => String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true, sensitivity: "base" });

function sheetIndex(sheets = []) {
  const byId = new Map();
  [...sheets].sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0) || a.SheetID - b.SheetID).forEach((s, i) => {
    byId.set(s.SheetID, { order: i, number: s.SheetNumber || "", name: s.SheetName || "" });
  });
  return byId;
}
const drawingOf = (idx, sheetId) => {
  const s = sheetId != null ? idx.get(sheetId) : null;
  return s ? { ...s } : { order: 1e9, number: "", name: NO_DRAWING };
};

// Accumulate readings: { q1, u1, q2, u2, q3, u3, n }
function addInto(acc, qs) {
  const [a, b, c] = qs;
  acc.q1 = sum(acc.q1, a?.q ?? null); acc.u1 = acc.u1 || a?.u || "";
  acc.q2 = sum(acc.q2, b?.q ?? null); acc.u2 = acc.u2 || b?.u || "";
  acc.q3 = sum(acc.q3, c?.q ?? null); acc.u3 = acc.u3 || c?.u || "";
  acc.n += 1;
  return acc;
}
const emptyAcc = () => ({ q1: null, u1: "", q2: null, u2: "", q3: null, u3: "", n: 0 });
const qCols = (acc) => ({ qty: r3(acc.q1), uom: acc.u1, instances: acc.n, qty2: r3(acc.q2), uom2: acc.u2, qty3: r3(acc.q3), uom3: acc.u3 });

const QTY_COLUMNS = [
  { key: "qty", label: "Quantity" }, { key: "uom", label: "UOM" }, { key: "instances", label: "Instances" },
  { key: "qty2", label: "Quantity 2" }, { key: "uom2", label: "UOM 2" }, { key: "qty3", label: "Quantity 3" }, { key: "uom3", label: "UOM 3" },
];

// ── the three reports ───────────────────────────────────────────────────────

/** Totals per condition, with where it was taken off ("A101: 5 ea; A102: 13 ea"). */
export function byConditionReport({ conditions, sheets, revision = "", units = "imperial" }) {
  const idx = sheetIndex(sheets);
  const groups = new Map();
  for (const c of conditions) {
    const { code, name, q } = rowQuantities(c, units);
    const k = conditionKey(c);
    const g = groups.get(k) || groups.set(k, { code, name, csi: c.CSICode ?? "", costCode: c.CostCodeDescription ?? "", total: emptyAcc(), perDrawing: new Map() }).get(k);
    addInto(g.total, q);
    const d = drawingOf(idx, c.SheetID);
    const label = d.number || d.name;
    const pd = g.perDrawing.get(label) || g.perDrawing.set(label, { order: d.order, acc: emptyAcc() }).get(label);
    addInto(pd.acc, q);
  }
  const rows = [...groups.values()].sort((a, b) => natural(a.code, b.code) || natural(a.name, b.name)).map(g => ({
    revision, code: g.code, name: g.name, csi: g.csi, costCode: g.costCode, ...qCols(g.total),
    drawings: [...g.perDrawing.entries()].sort((a, b) => a[1].order - b[1].order)
      .map(([label, d]) => `${label}: ${r3(d.acc.q1) ?? ""} ${d.acc.u1}`.trim()).join("; "),
  }));
  return {
    columns: [{ key: "revision", label: "Revision" }, { key: "code", label: "Condition Code" }, { key: "name", label: "Condition" }, ...QTY_COLUMNS, { key: "drawings", label: "By Drawing (Quantity 1)" }],
    rows,
  };
}

/** One row per drawing x condition. */
export function byDrawingReport({ conditions, sheets, revision = "", units = "imperial" }) {
  const idx = sheetIndex(sheets);
  const groups = new Map();
  for (const c of conditions) {
    const { code, name, q } = rowQuantities(c, units);
    const d = drawingOf(idx, c.SheetID);
    const k = `${c.SheetID ?? "none"}|${conditionKey(c)}`;
    const g = groups.get(k) || groups.set(k, { d, code, name, acc: emptyAcc() }).get(k);
    addInto(g.acc, q);
  }
  const rows = [...groups.values()]
    .sort((a, b) => a.d.order - b.d.order || natural(a.code, b.code) || natural(a.name, b.name))
    .map(g => ({ revision, drawingNumber: g.d.number, drawing: g.d.name, code: g.code, name: g.name, ...qCols(g.acc) }));
  return {
    columns: [{ key: "revision", label: "Revision" }, { key: "drawingNumber", label: "Drawing Number" }, { key: "drawing", label: "Drawing" }, { key: "code", label: "Condition Code" }, { key: "name", label: "Condition" }, ...QTY_COLUMNS],
    rows,
  };
}

/** One row per room x condition (a room with nothing drawn in it gets one row; conditions in no room are listed too). */
export function byRoomReport({ rooms, conditions, sheets, revision = "", units = "imperial" }) {
  const idx = sheetIndex(sheets);
  const roomById = new Map(rooms.map(r => [r.RoomID, r]));
  const groups = new Map(); // roomKey -> Map(condKey -> {code,name,acc})
  const keyOfRoom = (roomId, sheetId) => (roomId != null && roomById.has(roomId) ? `r${roomId}` : `x${sheetId ?? "none"}`);
  for (const c of conditions) {
    const { code, name, q } = rowQuantities(c, units);
    const rk = keyOfRoom(c.RoomID, c.SheetID);
    const per = groups.get(rk) || groups.set(rk, { sheetId: c.SheetID, conds: new Map() }).get(rk);
    const g = per.conds.get(conditionKey(c)) || per.conds.set(conditionKey(c), { code, name, acc: emptyAcc() }).get(conditionKey(c));
    addInto(g.acc, q);
  }

  const conv = { len: (v) => (units === "metric" ? v * FT_TO_M : v), area: (v) => (units === "metric" ? v * SF_TO_M2 : v) };
  const roomRow = (r) => {
    const d = drawingOf(idx, r.SheetID);
    const L = +r.LengthFt, W = +r.WidthFt, H = +r.HeightFt;
    const poly = polygonOf(r);
    let floor = null;
    if (L > 0 && W > 0) floor = poly ? polygonArea(poly) : calcAreas({ length: L, width: W, height: H > 0 ? H : 0 }).floorSF;
    return { d, base: { revision, drawingNumber: d.number, drawing: d.name, roomNumber: r.RoomNumber ?? "", room: r.RoomName ?? "", lengthFt: r3(L > 0 ? conv.len(L) : null), widthFt: r3(W > 0 ? conv.len(W) : null), heightFt: r3(H > 0 ? conv.len(H) : null), floorSf: r3(floor == null ? null : conv.area(floor)) } };
  };

  const out = [];
  const sortedRooms = [...rooms].sort((a, b) => drawingOf(idx, a.SheetID).order - drawingOf(idx, b.SheetID).order || natural(a.RoomNumber, b.RoomNumber) || (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0) || a.RoomID - b.RoomID);
  for (const r of sortedRooms) {
    const { d, base } = roomRow(r);
    const per = groups.get(`r${r.RoomID}`);
    const conds = per ? [...per.conds.values()].sort((a, b) => natural(a.code, b.code) || natural(a.name, b.name)) : [];
    if (!conds.length) out.push({ ...base, code: "", name: "", qty: null, uom: "", instances: null, qty2: null, uom2: "", qty3: null, uom3: "", _o: d.order });
    for (const g of conds) out.push({ ...base, code: g.code, name: g.name, ...qCols(g.acc), _o: d.order });
  }
  // conditions that sit in no room (e.g. a wall run that matched none)
  for (const [rk, per] of groups) {
    if (!rk.startsWith("x")) continue;
    const d = drawingOf(idx, per.sheetId);
    for (const g of [...per.conds.values()].sort((a, b) => natural(a.code, b.code))) {
      out.push({ revision, drawingNumber: d.number, drawing: d.name, roomNumber: "", room: NO_ROOM, lengthFt: null, widthFt: null, heightFt: null, floorSf: null, code: g.code, name: g.name, ...qCols(g.acc), _o: d.order + 0.5 });
    }
  }
  out.sort((a, b) => a._o - b._o); // stable: rooms keep their order inside a drawing; "no room" rows follow it
  return {
    columns: [
      { key: "revision", label: "Revision" }, { key: "drawingNumber", label: "Drawing Number" }, { key: "drawing", label: "Drawing" },
      { key: "roomNumber", label: "Room Number" }, { key: "room", label: "Room" },
      { key: "lengthFt", label: `Length (${unitWord("ft", units)})` }, { key: "widthFt", label: `Width (${unitWord("ft", units)})` }, { key: "heightFt", label: `Height (${unitWord("ft", units)})` }, { key: "floorSf", label: `Floor Area (${unitWord("SF", units)})` },
      { key: "code", label: "Condition Code" }, { key: "name", label: "Condition" }, ...QTY_COLUMNS,
    ],
    rows: out.map(({ _o, ...r }) => r),
  };
}

export const TAKEOFF_REPORTS = [
  { id: "room", label: "By room", file: "by-room", build: byRoomReport },
  { id: "drawing", label: "By drawing", file: "by-drawing", build: byDrawingReport },
  { id: "condition", label: "By condition", file: "by-condition", build: byConditionReport },
];

/** { filename, csv } for one report. */
export function buildTakeoffCsv(reportId, data, { project = "", revision = "", date = new Date(), units = "imperial" } = {}) {
  const def = TAKEOFF_REPORTS.find(r => r.id === reportId);
  if (!def) throw new Error(`Unknown takeoff report: ${reportId}`);
  const { columns, rows } = def.build({ ...data, revision, units });
  const stamp = new Date(date).toISOString().slice(0, 10);
  return { filename: csvFileName([project, revision, "takeoff", def.file, stamp]), csv: toCsv(columns, rows), rowCount: rows.length };
}
